(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.WOProductFitmentCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function scalar(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function identity(value) {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    var id = String(value).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    return /^[1-9]\d*$/.test(id) ? id : null;
  }

  function canonicalPattern(value) {
    if (typeof value !== 'string') return null;
    var match = value.trim().match(/^(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
    if (!match) return null;
    var holes = Number(match[1]), pcd = Number(match[2]);
    if (!Number.isSafeInteger(holes) || holes < 3 || holes > 12 || pcd < 40 || pcd > 400) return null;
    return holes + 'x' + pcd;
  }

  function optionName(value) {
    if (typeof value !== 'string') return null;
    var name = value.trim().toLowerCase().replace(/\s+/g, ' ');
    if (name === 'pcd / et') return 'pcd et';
    return name || null;
  }

  function normalizeVariant(raw, product) {
    var id = identity(raw && raw.id);
    function fail(reason) { return { variant: null, issue: { id: id, reason: reason } }; }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !id) return fail('invalid_variant');
    var entries;
    if (Array.isArray(raw.selectedOptions)) entries = raw.selectedOptions;
    else {
      var names = product && product.options;
      if (!Array.isArray(names) || !names.length) return fail('missing_options');
      var values = Array.isArray(raw.options) ? raw.options : names.map(function (_, index) { return raw['option' + (index + 1)]; });
      if (values.length !== names.length) return fail('malformed_options');
      entries = names.map(function (name, index) { return { name: typeof name === 'object' && name ? name.name : name, value: values[index] }; });
    }
    var options = Object.create(null);
    for (var entry of entries) {
      var name = optionName(entry && entry.name);
      if (!name || typeof entry.value !== 'string' || Object.prototype.hasOwnProperty.call(options, name)) return fail('ambiguous_options');
      options[name] = entry.value.trim();
    }
    var size = options.size || '';
    if (/\b(driver|passenger|left|right)\b/i.test(size)) return fail('directional_wheel');
    var dimensions = size.match(/^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
    if (!dimensions) return fail('missing_or_custom_size');
    var diameter = Number(dimensions[1]), width = Number(dimensions[2]);
    if (!(diameter > 0 && width > 0)) return fail('invalid_size');
    var pcd = options.pcd, et = options.et;
    if (options['pcd et'] !== undefined) {
      var combined = options['pcd et'].match(/^PCD:\s*(.*?)\s+ET:\s*(.*?)$/i);
      if (!combined || pcd !== undefined || et !== undefined) return fail('ambiguous_pcd_et');
      pcd = combined[1]; et = combined[2];
    }
    if (typeof pcd !== 'string' || typeof et !== 'string') return fail('missing_pcd_et');
    var offset = scalar(et.replace(/\s*mm\s*$/i, ''));
    if (offset === null) return fail('missing_or_custom_offset');
    var inchPatterns = { '4.5': 114.3, '4.75': 120.65, '5': 127, '5.5': 139.7, '6.5': 165.1 };
    var patterns = [];
    for (var part of pcd.split('/')) {
      var patternMatch = part.trim().match(/^(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
      if (!patternMatch) return fail('missing_or_custom_pcd');
      var numeric = Number(patternMatch[2]);
      var mm = numeric < 20 ? inchPatterns[String(numeric)] : numeric;
      var pattern = mm === undefined ? null : canonicalPattern(patternMatch[1] + 'x' + mm);
      if (!pattern) return fail('unsupported_pcd');
      if (!patterns.includes(pattern)) patterns.push(pattern);
    }
    return {
      variant: {
        id: id,
        title: typeof raw.title === 'string' ? raw.title : entries.map(function (item) { return item.value; }).join(' / '),
        sku: typeof raw.sku === 'string' ? raw.sku : '',
        available: raw.available === true || raw.availableForSale === true,
        diameter: diameter, width: width, offset: offset, patterns: patterns,
        finish: options.finish || null
      },
      issue: null
    };
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object' || !Array.isArray(product.variants)) {
      return { variants: [], issues: [{ id: null, reason: 'missing_product_variants' }] };
    }
    var variants = [], issues = [], counts = new Map();
    product.variants.forEach(function (raw) {
      var id = identity(raw && raw.id);
      if (id) counts.set(id, (counts.get(id) || 0) + 1);
    });
    product.variants.forEach(function (raw) {
      var result = normalizeVariant(raw, product);
      var id = identity(raw && raw.id);
      if (id && counts.get(id) > 1) {
        if (!issues.some(function (issue) { return issue.id === id; })) issues.push({ id: id, reason: 'duplicate_variant' });
      } else if (result.variant) variants.push(result.variant);
      else issues.push(result.issue);
    });
    return { variants: variants, issues: issues };
  }

  function dimensions(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value) || value.staggered === true || value.setup === 'staggered') return null;
    var diameter = scalar(value.diameter), width = scalar(value.width), offset = scalar(value.offset);
    return diameter !== null && diameter > 0 && width !== null && width > 0 && offset !== null
      ? { diameter: diameter, width: width, offset: offset } : null;
  }

  function policyOptions(options) {
    if (options === undefined) return { maxDiameterIncrease: 3 };
    if (!options || typeof options !== 'object' || Array.isArray(options) || Object.keys(options).some(function (key) { return key !== 'maxDiameterIncrease'; })) {
      throw new TypeError('Invalid fitment policy');
    }
    var increase = options.maxDiameterIncrease;
    if (increase === undefined) increase = 3;
    if (increase !== 0 && increase !== 1 && increase !== 2 && increase !== 3) throw new TypeError('Invalid diameter increase');
    return { maxDiameterIncrease: increase };
  }

  function sortMatches(a, b) {
    return a.specification.diameter - b.specification.diameter || a.specification.width - b.specification.width ||
      a.specification.offset - b.specification.offset || a.variant.id.localeCompare(b.variant.id);
  }

  function matchPool(boltPattern, references, variants, policy) {
    var stock = references.filter(function (reference) { return reference.stock; });
    var ceiling = stock.length ? Math.max.apply(null, stock.map(function (reference) { return reference.diameter; }))
      : Math.min.apply(null, references.map(function (reference) { return reference.diameter; }));
    var maxDiameter = ceiling + policy.maxDiameterIncrease;
    var diameterAnchors = stock.length ? stock : references.filter(function (reference) { return reference.diameter === ceiling; });
    var widest = Math.max.apply(null, references.map(function (reference) { return reference.width; }));
    var lateralAnchors = references.filter(function (reference) { return reference.width === widest; });
    var candidates = [], epsilon = 1e-9;
    variants.forEach(function (variant) {
      var specification = dimensions(variant);
      if (!specification || !identity(variant.id) || !Array.isArray(variant.patterns) || !variant.patterns.length) return;
      var patterns = variant.patterns.map(canonicalPattern);
      if (patterns.some(function (pattern) { return !pattern; }) || !patterns.includes(boltPattern)) return;
      var listed = references.filter(function (reference) { return reference.diameter === specification.diameter; });
      var explicitlyListed = listed.length > 0;
      var diameterReferences = explicitlyListed ? listed : policy.maxDiameterIncrease > 0 && specification.diameter <= maxDiameter + epsilon
        ? diameterAnchors.filter(function (reference) { return specification.diameter > reference.diameter + epsilon; }) : [];
      if (!diameterReferences.length) return;
      diameterReferences.sort(function (a, b) {
        return b.diameter - a.diameter || Number(b.stock) - Number(a.stock) || b.width - a.width || a.offset - b.offset || a.index - b.index;
      });
      var diameterReference = diameterReferences[0], best = null;
      var outwardLimit = specification.diameter === 15 || specification.diameter === 16 ? 70 : 40;
      lateralAnchors.forEach(function (reference) {
        var half = (specification.width - reference.width) * 12.7;
        var outwardMm = half + reference.offset - specification.offset;
        var inwardMm = half + specification.offset - reference.offset;
        if (!Number.isFinite(outwardMm) || !Number.isFinite(inwardMm) || outwardMm > outwardLimit + epsilon || inwardMm > 15 + epsilon) return;
        var score = Math.abs(outwardMm) + Math.abs(inwardMm);
        var order = best ? score - best.score || Math.abs(reference.offset - specification.offset) - Math.abs(best.reference.offset - specification.offset) ||
          Number(best.reference.stock) - Number(reference.stock) || Math.abs(reference.diameter - specification.diameter) - Math.abs(best.reference.diameter - specification.diameter) ||
          reference.diameter - best.reference.diameter || reference.offset - best.reference.offset || reference.index - best.reference.index : -1;
        if (!best || order < 0) best = { reference: reference, outwardMm: outwardMm, inwardMm: inwardMm, score: score };
      });
      if (!best) return;
      candidates.push({
        classification: 'suggested', variant: variant,
        specification: Object.assign({}, specification, { boltPattern: boltPattern }),
        reference: best.reference, outwardMm: best.outwardMm, inwardMm: best.inwardMm, score: best.score,
        limits: { outwardMm: outwardLimit, inwardMm: 15 }, diameterReference: diameterReference,
        rule: explicitlyListed ? 'listed' : 'plus-size',
        diameterIncrease: explicitlyListed ? 0 : specification.diameter - diameterReference.diameter,
        referenceBasis: diameterReference.stock ? 'stock' : 'listed',
        axle: 'both', fitmentScope: 'vehicle'
      });
    });
    return candidates.sort(sortMatches);
  }

  function match(vehicle, variants, options) {
    var policy = policyOptions(options);
    if (!vehicle || typeof vehicle !== 'object' || Array.isArray(vehicle) || !Array.isArray(variants) ||
      (vehicle.axleReferences !== undefined && !Array.isArray(vehicle.axleReferences))) throw new TypeError('Invalid vehicle reference');
    var rawPairs = vehicle.axleReferences || [], pairs = [], seen = new Set();
    rawPairs.forEach(function (source, index) {
      if (!source || typeof source !== 'object' || Array.isArray(source) || typeof source.id !== 'string' ||
        !source.id.trim() || source.id.length > 240 || !['square', 'staggered'].includes(source.setup)) return;
      var front = dimensions(source.front), rear = dimensions(source.rear);
      var frontPattern = canonicalPattern(source.front && source.front.boltPattern);
      var rearPattern = canonicalPattern(source.rear && source.rear.boltPattern);
      if (!front || !rear || !frontPattern || !rearPattern) return;
      if (seen.has(source.id)) throw new TypeError('Duplicate vehicle reference');
      seen.add(source.id);
      pairs.push({ id: source.id, index: index, source: source, stock: source.stock === true,
        front: Object.assign({}, front, { boltPattern: frontPattern }), rear: Object.assign({}, rear, { boltPattern: rearPattern }) });
    });
    var staggered = rawPairs.some(function (pair) { return pair && pair.setup === 'staggered'; }) || pairs.some(function (pair) {
      return pair.front.diameter !== pair.rear.diameter || pair.front.width !== pair.rear.width ||
        pair.front.offset !== pair.rear.offset || pair.front.boltPattern !== pair.rear.boltPattern;
    });
    if (!staggered) {
      var boltPattern = canonicalPattern(vehicle.boltPattern);
      if (!boltPattern || !Array.isArray(vehicle.wheels)) throw new TypeError('Missing wheel reference');
      var references = [];
      vehicle.wheels.forEach(function (source, index) {
        var specification = dimensions(source);
        if (specification) references.push(Object.assign({}, specification, { stock: source.stock === true, index: index }));
      });
      if (!references.length) throw new TypeError('Missing wheel reference');
      return matchPool(boltPattern, references, variants, policy);
    }
    if (!pairs.length) throw new TypeError('Missing front and rear reference');
    var byVariant = new Map();
    ['front', 'rear'].forEach(function (axle) {
      var pools = new Map();
      pairs.forEach(function (pair) {
        var side = pair[axle], pattern = side.boltPattern;
        if (!pools.has(pattern)) pools.set(pattern, []);
        pools.get(pattern).push(Object.assign({}, side, { stock: pair.stock, index: pair.index, pairId: pair.id, axle: axle }));
      });
      pools.forEach(function (references, pattern) {
        matchPool(pattern, references, variants, policy).forEach(function (candidate) {
          var scoped = Object.assign({}, candidate, { axle: axle, fitmentScope: 'axle', referencePairId: candidate.reference.pairId });
          var id = candidate.variant.id;
          if (!byVariant.has(id)) byVariant.set(id, {});
          var existing = byVariant.get(id)[axle];
          if (!existing || scoped.score < existing.score || scoped.score === existing.score && scoped.referencePairId.localeCompare(existing.referencePairId) < 0) {
            byVariant.get(id)[axle] = scoped;
          }
        });
      });
    });
    var matches = [];
    byVariant.forEach(function (axleMatches) {
      matches.push(Object.assign({}, axleMatches.front || axleMatches.rear, {
        axle: axleMatches.front && axleMatches.rear ? 'both' : axleMatches.front ? 'front' : 'rear',
        fitmentScope: 'axle', axleMatches: axleMatches
      }));
    });
    return matches.sort(sortMatches);
  }

  function check(vehicle, normalized, selectedVariantId, options) {
    var id = identity(selectedVariantId), variants = normalized && normalized.variants;
    var issues = normalized && normalized.issues || [];
    var result = { status: 'unknown', selected: null, matches: [], alternatives: [], issue: null };
    if (!Array.isArray(variants)) { result.issue = { id: id, reason: 'missing_product_variants' }; return result; }
    var selectedVariants = id ? variants.filter(function (variant) { return variant.id === id; }) : [];
    if (selectedVariants.length !== 1) {
      result.issue = issues.find(function (issue) { return issue.id === id; }) || { id: id, reason: 'unknown_variant' };
    }
    try { result.matches = match(vehicle, variants, options); }
    catch (_) { result.issue = { id: id, reason: 'unsupported_vehicle_reference' }; return result; }
    result.alternatives = result.matches.filter(function (candidate) { return candidate.variant.id !== id && candidate.variant.available; });
    if (result.issue) return result;
    result.selected = result.matches.find(function (candidate) { return candidate.variant.id === id; }) || null;
    result.status = result.selected ? 'suggested' : 'not_suggested';
    return result;
  }

  return Object.freeze({ normalizeProduct: normalizeProduct, normalizeVariant: normalizeVariant, match: match, check: check });
});

